import {
  FILTER_OPTION_SEARCH_MIN_COUNT,
  foldFilterSearchText,
  showsFilterOptionSearch,
} from '@core/shared-bridge';

export {
  FILTER_OPTION_SEARCH_MIN_COUNT,
  foldFilterSearchText,
  showsFilterOptionSearch,
};

/** Možnost ve filtru s více hodnotami. */
export interface FilterMultiOption {
  value: string;
  label: string;
  /**
   * Volitelná skupina (optgroup). Stejný `group` u po sobě jdoucích položek
   * vykreslí společnou hlavičku v `app-filter-multi-select`.
   */
  group?: string;
  /** Druhý řádek pod `label` (např. název stavby pod KIA). */
  subtitle?: string;
  /**
   * Text navíc pro hledání v panelu, když se liší od viditelného `label`
   * (např. název stavby u filtru, který zobrazuje jen KIA).
   */
  searchText?: string;
  /** Optional tone class on the primary label (e.g. product-category filter). */
  labelClass?: string;
}

/** Text na tlačítku filtru (např. „Typy“ / „3 vybráno“). */
export function filterMultiSummary(
  selected: string[],
  options: FilterMultiOption[],
  emptyLabel: string,
): string {
  if (!selected?.length) return emptyLabel;
  if (selected.length === 1) {
    const hit = options.find((o) => o.value === selected[0]);
    return hit?.label ?? selected[0];
  }
  return `${selected.length} vybráno`;
}

/** CSV pro query param (BE akceptuje `a,b` i opakovaný param). */
export function joinMultiFilterCsv(values: string[] | undefined | null): string | undefined {
  const v = (values ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
  return v.length > 0 ? [...new Set(v)].join(',') : undefined;
}

/** Client-side: řádek projde, pokud je filtr prázdný nebo hodnota je ve výběru. */
export function matchesMultiValueFilter(value: string | null | undefined, selected: string[]): boolean {
  if (!selected.length) return true;
  const v = String(value ?? '').trim();
  return selected.includes(v);
}

/** Client-side: alespoň jedna hodnota z `values` je ve výběru (např. materiál / aktivita). */
export function matchesMultiAnyFilter(values: string[] | undefined | null, selected: string[]): boolean {
  if (!selected.length) return true;
  const set = new Set((values ?? []).map((x) => String(x ?? '').trim()).filter(Boolean));
  return selected.some((s) => set.has(s));
}

/**
 * Sloučí možnosti pro multi-select filtr: dříve známé hodnoty, hodnoty z aktuálních řádků
 * a aktuálně vybrané. Seznam možností nesmí zužovat po aplikaci filtru (jinak nelze
 * pohodlně vybrat více hodnot najednou).
 */
export function mergeFacetFilterValues(
  known: Iterable<string> | null | undefined,
  fromRows: Iterable<string | null | undefined>,
  selected: Iterable<string> | null | undefined,
): string[] {
  const set = new Set<string>();
  for (const source of [known, fromRows, selected]) {
    if (!source) continue;
    for (const raw of source) {
      const v = String(raw ?? '').trim();
      if (v) set.add(v);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'cs'));
}

/** Převod hodnot na možnosti pro `app-filter-multi-select`. */
export function toFilterMultiOptions(values: string[]): FilterMultiOption[] {
  return values.map((value) => ({ value, label: value }));
}

/**
 * Zúží možnosti filtru podle textu. Prázdný dotaz vrátí původní seznam.
 * Vybrané hodnoty zůstanou vidět, i když dotazu neodpovídají.
 * Hledání v panelu se zobrazí jen když `showsFilterOptionSearch(options.length)`.
 */
export function filterMultiOptionsByQuery(
  options: FilterMultiOption[],
  query: string,
  selected: readonly string[] = [],
): FilterMultiOption[] {
  const q = foldFilterSearchText(query);
  if (!q) return options;
  const selectedSet = new Set(selected);
  return options.filter((opt) => {
    if (selectedSet.has(opt.value)) return true;
    const hay = foldFilterSearchText(
      `${opt.label} ${opt.subtitle ?? ''} ${opt.group ?? ''} ${opt.searchText ?? ''}`,
    );
    return hay.includes(q);
  });
}
